import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { groupIntoProducts } from "../services/catalogueService";
import "./CatalogueModal.css";

/**
 * Product catalogue for one Sourcing category.
 *
 * Two levels: a grid of products, and one product enlarged. A product is
 * usually one image, but where several images are views of the same garment
 * (front, back, detail) they are grouped — the grid shows one tile and the
 * other views are reached by opening it, rather than repeating the garment
 * across the grid.
 *
 * Escape closes whichever level is showing, so the grid isn't skipped when
 * backing out of a product.
 */
const CatalogueModal = ({ category, images, onClose }) => {
  const products = useMemo(() => groupIntoProducts(images), [images]);

  // null = grid. Otherwise the product being viewed, and which of its views.
  const [openIndex, setOpenIndex] = useState(null);
  const [viewIndex, setViewIndex] = useState(0);

  const product = openIndex !== null ? products[openIndex] : null;
  const views = product ? product.images : [];
  const viewCount = views.length;

  const nextView = useCallback(
    () => setViewIndex((i) => (i + 1) % viewCount),
    [viewCount]
  );
  const prevView = useCallback(
    () => setViewIndex((i) => (i - 1 + viewCount) % viewCount),
    [viewCount]
  );

  const openProduct = (i) => {
    setOpenIndex(i);
    setViewIndex(0);
  };

  // Keyboard: Escape steps back one level, arrows move between this product's
  // views. Arrows do nothing on a single-view product, which is correct — there
  // is nowhere to go without silently jumping to a different garment.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (openIndex !== null) setOpenIndex(null);
        else onClose();
      } else if (openIndex !== null && viewCount > 1) {
        if (e.key === "ArrowRight") nextView();
        if (e.key === "ArrowLeft") prevView();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex, viewCount, nextView, prevView, onClose]);

  // Stop the page behind the modal from scrolling.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const active = product ? views[viewIndex] : null;

  return (
    <div
      className="cat-modal-backdrop"
      onMouseDown={() => (openIndex !== null ? setOpenIndex(null) : onClose())}
      role="dialog"
      aria-modal="true"
      aria-label={`${category.title} catalogue`}
    >
      <div className="cat-modal" onMouseDown={(e) => e.stopPropagation()}>
        <header className="cat-modal-head">
          <div>
            <span className="cat-modal-eyebrow">Product Catalogue</span>
            <h2>{category.title}</h2>
          </div>
          <button className="cat-modal-close" onClick={onClose} aria-label="Close catalogue">
            &times;
          </button>
        </header>

        {products.length === 0 ? (
          /* Fallback: nothing uploaded for this category yet. */
          <div className="cat-modal-empty">
            <p className="cat-empty-title">Catalogue coming soon</p>
            <p className="cat-empty-text">
              We have not yet published images for {category.title.toLowerCase()}. Our
              merchandising team can send the current range, including
              specifications and pricing, on request.
            </p>
            <Link to="/contact" className="cat-empty-link" onClick={onClose}>
              Request the {category.title} range &rarr;
            </Link>
          </div>
        ) : product ? (
          /* One product, enlarged */
          <div className="cat-single">
            <button className="cat-back" onClick={() => setOpenIndex(null)}>
              &larr; All {category.title.toLowerCase()}
            </button>

            <div className="cat-single-stage">
              {viewCount > 1 && (
                <button className="cat-nav prev" onClick={prevView} aria-label="Previous view">
                  &#8249;
                </button>
              )}
              <img src={active.imageUrl} alt={active.caption || product.caption || category.title} />
              {viewCount > 1 && (
                <button className="cat-nav next" onClick={nextView} aria-label="Next view">
                  &#8250;
                </button>
              )}
            </div>

            {viewCount > 1 && (
              <ul className="cat-views" aria-label="Other views of this product">
                {views.map((v, i) => (
                  <li key={v.id}>
                    <button
                      className={`cat-view-thumb ${i === viewIndex ? "on" : ""}`}
                      onClick={() => setViewIndex(i)}
                      aria-label={`View ${i + 1} of ${viewCount}`}
                      aria-current={i === viewIndex}
                    >
                      <img src={v.imageUrl} alt="" loading="lazy" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="cat-single-foot">
              {(active.caption || product.caption) && (
                <p className="cat-single-caption">{active.caption || product.caption}</p>
              )}
              {viewCount > 1 && (
                <span className="cat-single-count">{viewIndex + 1} / {viewCount}</span>
              )}
            </div>
          </div>
        ) : (
          /* Grid of products */
          <>
            <p className="cat-modal-sub">
              {products.length} {products.length === 1 ? "product" : "products"} shown.
              Select any product to enlarge it.
            </p>
            <ul className="cat-modal-grid">
              {products.map((p, i) => (
                <li key={p.key}>
                  <button
                    className="cat-tile"
                    onClick={() => openProduct(i)}
                    aria-label={
                      p.caption
                        ? `${p.caption}${p.images.length > 1 ? `, ${p.images.length} views` : ""}`
                        : `${category.title} product ${i + 1}`
                    }
                  >
                    <span className="cat-tile-media">
                      <img src={p.cover.imageUrl} alt={p.caption || ""} loading="lazy" />
                      {/* Second view revealed on hover, so a grouped product
                          shows it has more than one image without a click. */}
                      {p.images.length > 1 && (
                        <img
                          className="cat-tile-peek"
                          src={p.images[1].imageUrl}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                        />
                      )}
                      {p.images.length > 1 && (
                        <span className="cat-tile-badge">{p.images.length} views</span>
                      )}
                    </span>
                    {p.caption && <span className="cat-tile-caption">{p.caption}</span>}
                  </button>
                </li>
              ))}
            </ul>
            <div className="cat-modal-foot">
              <Link to="/contact" className="cat-empty-link" onClick={onClose}>
                Enquire about {category.title.toLowerCase()} &rarr;
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CatalogueModal;
